import { remoteHttpClientLayer } from "@amoreg/client-runtime/rpc";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import { FetchHttpClient, HttpClient, HttpClientRequest } from "effect/unstable/http";

import { readDesktopPrimaryBearerToken } from "./desktopAuth";
import { resolvePrimaryEnvironmentHttpUrl } from "./target";

/**
 * True when the primary environment authenticates with the browser session
 * cookie (same origin, or a configured backend on the same registrable site).
 * When it is false the session rides an `Authorization: Bearer` header, which
 * a plain subresource — an `<img>` — cannot carry.
 */
export function primaryHttpUsesBrowserSession(): boolean {
  if (
    typeof window === "undefined" ||
    window.desktopBridge !== undefined ||
    !window.location.origin.startsWith("http")
  ) {
    return false;
  }

  const target = new URL(resolvePrimaryEnvironmentHttpUrl("/"));
  if (target.origin === window.location.origin) {
    return true;
  }
  // A configured backend on the same registrable site (dev.amoreg.com →
  // server.amoreg.com) still authenticates with the browser-session cookie:
  // SameSite=Lax cookies flow on same-site cross-origin requests, and the
  // server allowlists the app origin for credentialed CORS via its dev-URL
  // config. Cross-site targets keep the desktop bearer path. The naive
  // two-label site check is enough for the domains this fork deploys to.
  const site = (hostname: string) => hostname.split(".").slice(-2).join(".");
  return (
    target.protocol === "https:" &&
    window.location.protocol === "https:" &&
    site(target.hostname) === site(window.location.hostname)
  );
}

function withPrimaryBearerToken(client: HttpClient.HttpClient): HttpClient.HttpClient {
  return client.pipe(
    HttpClient.mapRequestEffect((request) =>
      Effect.promise(readDesktopPrimaryBearerToken).pipe(
        Effect.map((bearerToken) =>
          bearerToken ? HttpClientRequest.bearerToken(request, bearerToken) : request,
        ),
      ),
    ),
  );
}

// FetchHttpClient reads its RequestInit service from the executing fiber's
// context at request time; the runtime composes this layer via Layer.provide,
// which never exposes that service to running fibers — so a merged RequestInit
// is inert. Baking credentials into the fetch function itself is the only
// wiring that reliably reaches every request (cookie auth needs `include`
// cross-origin within the same site).
const fetchWithCredentials: typeof globalThis.fetch = (input, init) =>
  globalThis.fetch(input, { ...init, credentials: "include" });

export function makePrimaryEnvironmentHttpLayer() {
  return Layer.unwrap(
    Effect.sync(() => {
      if (primaryHttpUsesBrowserSession()) {
        return Layer.merge(
          remoteHttpClientLayer(fetchWithCredentials),
          Layer.succeed(FetchHttpClient.RequestInit, { credentials: "include" }),
        );
      }
      const baseLayer = remoteHttpClientLayer(globalThis.fetch);

      const bearerClientLayer = Layer.effect(
        HttpClient.HttpClient,
        Effect.map(HttpClient.HttpClient, withPrimaryBearerToken),
      ).pipe(Layer.provide(baseLayer));

      return Layer.merge(
        bearerClientLayer,
        Layer.succeed(FetchHttpClient.RequestInit, { credentials: "omit" }),
      );
    }),
  );
}

export const primaryEnvironmentHttpLayer = makePrimaryEnvironmentHttpLayer();
